import {
  Grid,
  Box,
  Skeleton,
  Alert
} from "@mui/material";
import { ResponsiveContainer, Area, AreaChart } from 'recharts';
import {
  AssessmentOutlined
} from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import Colors from "../../../../constants/Colors";
import Paper from "@mui/material/Paper";
import {CARD_CAPTION_STYLE} from "../../../../constants/Styles";
import {ContainedButton} from "../../../common/StyledButtons";
import {withRouter} from "react-router";
import {getLiteSiteAnalyticsByIdAndRange} from "../../../../selectors/SiteSelectors";
import {observer} from "mobx-react-lite";
import { calculateStartDate, formatDateToYYYYMMDD, formatSecondsToPrettyMSTime } from "../../../../utilities/DateUtilities";
import { orderBy } from "lodash";
import StyledSection from "../../../common/StyledSection";
import { getQuickSiteAnalytics } from "../../../../actions/internal/SiteActions";
import { getUserId } from "../../../../selectors/UserSelectors";

const ActivityCard = ({ history, siteId }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const liteAnalytics = getLiteSiteAnalyticsByIdAndRange(siteId, 30);

  useEffect(() => {
    prepareSiteSession();
  }, [siteId]);

  const prepareSiteSession = async () => {
    try {
      setIsLoading(true);
      setError(null); // reset error before new fetch
      const userId = getUserId();
      await fetchSiteAnalytics(siteId, userId);
    } catch (ex) {
      console.error("Error fetching analytics:", ex);
      setError("Uh oh! We could not load your activity data at this time. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSiteAnalytics = async (siteId, userId) => {
    const date = new Date();
    const PAST_DAYS = 30;
    const startDate = calculateStartDate(date.getFullYear(), date.getMonth() + 1, date.getDate(), PAST_DAYS);
    const endDate = formatDateToYYYYMMDD(date);
    const defaultOptions = { startDate, endDate, includeRecentReports: true };
    await getQuickSiteAnalytics(userId, siteId, defaultOptions, PAST_DAYS);
  };

  const hasAnalytics = () => liteAnalytics && liteAnalytics.statistics;

  const prepareFormattedTime = () => {
    if (!hasAnalytics()) return "---";
    return formatSecondsToPrettyMSTime(liteAnalytics.statistics.session_time_avg);
  };

  const prepareVisitorData = () => {
    if (!liteAnalytics || !liteAnalytics.statistics) return [];
    const sortedTarget = orderBy(liteAnalytics.analytics, ["epoch"], ["desc"]);
    return sortedTarget.map((record) => ({
      uv: record.activeUsers
    }));
  };

  const renderVisitorsSection = () => {
    // Guard all the way down
    const stats =
    liteAnalytics &&
    liteAnalytics.statistics
        ? liteAnalytics.statistics
        : null;
    
    const items = [
      {
        label: "New Visitors",
        value: stats && stats.new_users_total ? stats.new_users_total : "---"
      },
      {
        label: "Total Visitors",
        value: stats && stats.active_users_total ? stats.active_users_total : "---"
      },
      {
        label: "Avg. Duration",
        value:
          stats && typeof stats.session_time_avg === "number"
            ? formatSecondsToPrettyMSTime(stats.session_time_avg)
            : "---"
      }
    ];
  
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          position: "relative",
          zIndex: 15,
          gap: "24px",
          width: "100%"
        }}
      >
        {items.map(({ label, value }) => (
          <Box
            key={label}
            onClick={() => history.push(`/dashboard/site/${siteId}/activity`)}
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              border: "1px solid #ddd",
              borderRadius: "8px",
              padding: "16px",
              transition: "all 0.2s ease",
              cursor: "pointer",
              backgroundColor: "rgba(255, 255, 255, 0.85)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                transform: "scale(1.02)"
              }
            }}
          >
            <h6 style={{ fontSize: "18px", margin: 0 }}>{label}</h6>
            <h3 style={{ margin: "8px 0 0 0", fontSize: "36px" }}>{value}</h3>
          </Box>
        ))}
      </Box>
    );
  };
  

  const renderLoadingSection = () => (
    <Box sx={{ padding: "16px"}} >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          position: "relative",
          zIndex: 15,
          gap: "24px",
          width: "100%",
        }}
      >
        {["New Visitors", "Total Visitors", "Avg. Duration"].map((label) => (
          <Box
            key={label}
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              border: "1px solid #ddd",
              borderRadius: "8px",
              padding: "16px",
              backgroundColor: "rgba(255, 255, 255, 1)",
            }}
          >
            <h6 style={{ fontSize: "18px", margin: 0 }}>{label}</h6>
            <Skeleton height={75} width={200} sx={{ marginTop: "8px" }} />
          </Box>
        ))}
      </Box>
    </Box>
  );

  const renderDataSection = () => (
    <Box sx={{ position: "relative" }}>
      <Box sx={{ padding: "16px" }}>
        {renderVisitorsSection()}
        <Box sx={{ zIndex: 15 }}>
          {error && (
            <Box sx={{ padding: "16px" }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}
          {(!error && (!liteAnalytics || !liteAnalytics.enabled)) && 
          <Box sx={{ padding: "16px" }}>
            <Alert severity="warning">Activity monitoring is not currently enabled for your account.</Alert>
          </Box>}
        </Box>
      </Box>
      <Box sx={{ position: "absolute", bottom: 0, left: 0, width: "100%", padding: 0}}>
        <ResponsiveContainer width="100%" height={200} style={{ padding: 0 }}>
          <AreaChart data={prepareVisitorData()}>
            <Area type="monotone" dataKey="uv" stroke={Colors.primaryColor} dot={false} fill={Colors.primaryColor} fillOpacity={0.05} />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );

  return (
    <StyledSection
      title="Activity"
      paperStyling={{padding: 0}}
      titleStyling={{ padding: "16px 16px 0px 16px" }}
    >
      {isLoading && renderLoadingSection()}
      {!isLoading && renderDataSection()}
      
    </StyledSection>
  )
}

export default observer(withRouter(ActivityCard));
